import React, {ReactNode, useEffect} from 'react'
import queryString from 'query-string'
import classNames from 'classnames'
import {Box, Flex, Stack, Text, useDisclosure, BaseButton} from '../../../../vanilla'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MapMarkerLightIcon,
  PhoneIcon,
} from '../../../../components/icons'
import {Link} from '../../../../components/link'
import {CollapsablePanel} from '../../../../components/collapsable-panel'
import {StoreDetailResponseItem} from '../../../../types/store/storeDetails'
import {detailInfoContainer, marker, markerSelected, storeName, workingSchedule} from './styles.css'
import useCustomSitePreference from '../../../../hooks/forms/useCustomSitePreference'

interface MarkerProps {
  selected: boolean
  num: ReactNode
  onClick: () => void
}

const Marker = ({num, selected, onClick}: MarkerProps) => {
  return (
    <Box
      onClick={onClick}
      className={classNames(marker, {[markerSelected]: selected})}
      cursor="pointer"
    >
      <Text variant="text6" lineHeight="none" color="white" width="full" as="span" align="center">
        {num}
      </Text>
    </Box>
  )
}
interface DetailsInfoProps {
  details: StoreDetailResponseItem
  url: string
}

const DetailsInfo = ({details, url}: DetailsInfoProps) => {
  const currentDayOfWeekName = new Date().toLocaleString('default', {weekday: 'long'})
  const enableSeasonalOpeningHours = useCustomSitePreference('enableSeasonalOpeningHours', false)
  const seasonalOpeningHoursText = useCustomSitePreference('seasonalOpeningHoursText', "")
  
  return (
    <Stack
      className={detailInfoContainer}
      flexDirection={['column', 'row']}
      spacing="0px"
      justifyContent="space-between"
    >
      <Stack>
        <Flex
          gap="8px"
          align="flex-start"
          display={['block', 'flex']}
          paddingX={['16px', '0px']}
          paddingY={['20px', '0px']}
          borderBottom={['1px', '0px']}
          borderColor="gray200"
        >
          <MapMarkerLightIcon
            color="gray800"
            style={{width: 22, height: 'auto'}}
            display={['none', 'inline-block']}
          />
          <Stack direction={['row', 'column']} spacing={['4px', '0px']}>
            {details.address1 && (
              <Text variant="text3" lineHeight="shorter" as="span">
                {details.address1}
              </Text>
            )}
            {details.address2 && (
              <Text variant="text3" lineHeight="shorter" as="span">
                {details.address2}
              </Text>
            )}
            {details.city && (
              <Text variant="text3" lineHeight="shorter" as="span">
                {details.city}
              </Text>
            )}
            {details.postalCode && (
              <Text variant="text3" lineHeight="shorter" as="span">
                {details.postalCode}
              </Text>
            )}
          </Stack>
        </Flex>
        {details.phone && (
          <Flex
            gap="8px"
            align="flex-start"
            display={['none', 'flex']}
            paddingX={['16px', '0px']}
            paddingY={['20px', '0px']}
          >
            <PhoneIcon boxSize="20px" color="gray800" />
            <Text variant="text3" lineHeight="shorter" as="span">
              {details.phone}
            </Text>
          </Flex>
        )}
      </Stack>

      {details.phone && (
        <Flex
          borderTop="1px"
          borderBottom="1px"
          borderColor="gray200"
          paddingX="16px"
          paddingY="20px"
          align="center"
          gap="8px"
          display={['flex', 'none']}
          order="2"
        >
          <PhoneIcon boxSize="20px" color="gray800" />
          <Text variant="text3" lineHeight="shorter" as="span">
            {details.phone}
          </Text>
        </Flex>
      )}
      {(details.c_storeFeatures?.length || enableSeasonalOpeningHours) && (
        <Stack
          spacing="4px"
          order={['2', '0']}
          paddingX={['16px', '0px']}
          paddingY={['20px', '0px']}
        >
          <Text variant="text4" as="span" lineHeight="shorter">
            Store Details
          </Text>
          <Stack as="ul" spacing="8px">
            {details.c_storeFeatures?.length && details.c_storeFeatures.map((feature) => (
              <Flex as="li" align="center" gap="4px" key={`${details.id}-${feature.value}`}>
                <CheckIcon boxSize="20px" color="accent1" />
                <Text variant="text4" as="span" lineHeight="shorter">
                  {feature.displayValue}
                </Text>
              </Flex>
            ))}
          </Stack>
          {enableSeasonalOpeningHours && seasonalOpeningHoursText != "" &&
          <Text 
            color='accent1' 
            style={{maxWidth: 200}}
            as={Link}
            to={url}
            textDecoration="underline"
          >View {seasonalOpeningHoursText}</Text>}
        </Stack>
      )}
      {details.c_storeHoursJson?.length && (
        <Stack
          spacing="0px"
          className={workingSchedule}
          paddingX={['16px', '0px']}
          paddingY={['20px', '0px']}
          style={{minHeight: 130}}
        >
          {details.c_storeHoursJson.map((hours, idx) => (
            <Flex
              width="full"
              align="center"
              justify="space-between"
              key={`${details.id}-hours-${idx}`}
            >
              <Text
                variant={hours.day === currentDayOfWeekName.toLowerCase() ? 'text3Bold' : 'text3'}
                lineHeight="shorter"
                as="span"
                textTransform={'capitalize'}
                color='gray800'
              >
                {hours.day}
              </Text>

              <Text
                variant={hours.day === currentDayOfWeekName.toLowerCase() ? 'text3Bold' : 'text3'}
                lineHeight="shorter"
                as="span"
                color='gray800'
              >
                {hours.open} - {hours.close}
              </Text>
            </Flex>
          ))}
        </Stack>
      )}
      <Box alignSelf="center" display={['none', 'block']} marginTop={['0px', '16px', '0px']}>
        <BaseButton
          variant="secondary"
          size="sm"
          as={Link}
          to={url}
          display="inline-flex"
          gap="20px"
        >
          <Text variant="unstyled" fontSize="inherit" fontWeight="inherit" lineHeight="shorter">
            View Store Details
          </Text>
          <ChevronRightIcon boxSize="12px" color="white" />
        </BaseButton>
      </Box>
    </Stack>
  )
}

interface MobileCollapsablePanelHeaderProps extends ReturnType<typeof useDisclosure> {
  isStoreSelected: boolean
  name: string
  distance: number
  handleMarkerClick: (id: string) => void
  num: number
  storeId: string
  url: string
}

const MobileCollapsablePanelHeader = (props: MobileCollapsablePanelHeaderProps) => {
  const {
    onToggle,
    name,
    distance,
    isStoreSelected,
    onOpen,
    storeId,
    handleMarkerClick,
    isOpen,
    num,
    url,
  } = props

  useEffect(() => {
    if (isStoreSelected) onOpen()
  }, [isStoreSelected])

  return (
    <Flex
      padding="20px"
      align="center"
      borderBottom="1px"
      borderColor="gray200"
      justify="space-between"
      onClick={() => onToggle()}
    >
      <Flex gap="4px" align="center">
        <Flex gap="16px" align="center">
          <Marker num={num} selected={isStoreSelected} onClick={() => handleMarkerClick(storeId)} />
          <Text as="span" variant="text4" lineHeight="shorter">
            <Text variant="text4" lineHeight="shorter" as={Link} to={url} className={storeName}>
              {name}
            </Text>
            {typeof distance === 'number' && distance !== 0 && (
              <Text as="span" variant="text4" lineHeight="shorter">
                {' '}
                - ({distance} miles)
              </Text>
            )}
          </Text>
        </Flex>
      </Flex>

      <ChevronDownIcon boxSize="16px" style={{transform: isOpen ? 'rotate(180deg)' : 'none'}} />
    </Flex>
  )
}

interface StoreDetailItemProps {
  store: StoreDetailResponseItem
  selectedStoreId: string
  num: number
  handleMarkerClick: (storeId: string) => void
  searchVal?: string
}

export const StoreDetailItem = (props: StoreDetailItemProps) => {
  const {store, num, handleMarkerClick, selectedStoreId, searchVal} = props

  const {name, distance, id} = store

  const buildStoreDetailUrl = (ID: string, name: string, searchVal?: string) => {
    const url = '/store-finder/store'

    const params = queryString.stringify(
      {
        StoreID: ID,
        StoreName: encodeURIComponent(name),
        searchVal: encodeURIComponent(searchVal || ''),
      },
      {skipEmptyString: true},
    )

    return url + '?' + params
  }

  return (
    <>
      {/* Desktop store item layout */}
      <Stack spacing="0px" bg="white" display={['none', 'flex']}>
        <Flex padding="20px" align="center" borderBottom="1px" borderColor="gray200" gap="16px">
          <Marker
            num={num}
            onClick={() => handleMarkerClick(store.id)}
            selected={selectedStoreId === store.id}
          />
          <Text as="span" variant="text4" lineHeight="shorter">
            <Text
              variant="text4"
              lineHeight="shorter"
              as={Link}
              to={buildStoreDetailUrl(id, name, searchVal)}
              className={storeName}
            >
              {name}
            </Text>
            {typeof distance === 'number' && distance !== 0 && (
              <Text as="span" variant="text4" lineHeight="shorter">
                {' '}
                - ({distance} miles)
              </Text>
            )}
          </Text>
        </Flex>
        <DetailsInfo details={store} url={buildStoreDetailUrl(id, name, searchVal)} />
      </Stack>
      {/* Mobile store item layout */}
      <CollapsablePanel
        defaultOpen={false}
        panelContent={
          <Box padding="20px" bg="gray50">
            <DetailsInfo details={store} url={buildStoreDetailUrl(id, name, searchVal)} />
          </Box>
        }
        bg="white"
        display={['block', 'none']}
      >
        {(disclosureProps) => (
          <MobileCollapsablePanelHeader
            storeId={store.id}
            num={num}
            name={name}
            distance={distance}
            isStoreSelected={store.id === selectedStoreId}
            handleMarkerClick={handleMarkerClick}
            url={buildStoreDetailUrl(id, name, searchVal)}
            {...disclosureProps}
          />
        )}
      </CollapsablePanel>
    </>
  )
}
